import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { InputNumberMask } from '../../../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Label } from '../../../../../../../../CommonComponents/OPL/Label/Label';
import { Text } from '../../../../../../../../CommonComponents/OPL/Text/Text';
import { formatCurrency } from '../../../../../../../../CommonUtils/formatNumber';
import { ACCOUNTS_WITH_HOLDINGS_TAB, AnnuityType as AnnuityTypes } from '../../AccountForm.meta';
import { parsePositionValue } from '../../utils/parsePositionValue';
import { Box } from '../../../../../../../../CommonComponents/OPL/Box/Box';
import { Dot } from '../../../../../../../../CommonComponents/OPL/Dot/Dot';

interface BalanceProps {
  label?: string;
}

export const Balance = ({ label = 'Balance' }: BalanceProps) => {
  const formBag = useFormContext();

  const { watch } = formBag;

  const [
    accountType,
    specificPositionsValue,
    nonSpecificPositionsValue,
    annuityType,
    isLinked,
    isOrionManagedAccount,
  ] = watch([
    'bankAccount.AccountType',
    'bankAccount.SpecificPositionsValue',
    'bankAccount.NonSpecificPositionsValue',
    'bankAccount.AnnuityType',
    'bankAccount.IsLinked',
    'bankAccount.IsOrionManagedAccount',
  ]);

  const isSyncedAccount = isLinked || isOrionManagedAccount;

  const showMarketValueBreakdown =
    ACCOUNTS_WITH_HOLDINGS_TAB.includes(accountType) &&
    parsePositionValue(specificPositionsValue) > 0 &&
    annuityType !== AnnuityTypes.Fixed;

  return (
    <>
      {showMarketValueBreakdown && (
        <Controller
          control={formBag.control}
          name="bankAccount.SpecificPositionsValue"
          render={({ field }) => (
            <InputNumberMask
              {...field}
              disabled
              leftIcon="fa fa-dollar-sign"
              label={
                <>
                  Specific Holdings
                  {isSyncedAccount && (
                    <Box
                      display="inline-flex"
                      alignItems="center"
                      css="gap: 4px"
                      bg="success.100"
                      px="4"
                      py="2"
                      ml="8"
                    >
                      <Dot size={6} bg="success.500" />
                      <Text variant="formLabel" color="success.700">
                        Synced
                      </Text>
                    </Box>
                  )}
                </>
              }
              allowNegative={false}
              decimalScale={2}
            />
          )}
        />
      )}

      <Controller
        control={formBag.control}
        name="bankAccount.NonSpecificPositionsValue"
        render={({ field: { onChange, ...rest } }) => (
          <InputNumberMask
            {...rest}
            leftIcon="fa fa-dollar-sign"
            label={showMarketValueBreakdown ? 'Non-specific Holdings' : label}
            allowNegative={false}
            decimalScale={2}
            onValueChange={({ floatValue }) => {
              onChange(floatValue ?? null);
            }}
          />
        )}
      />

      {showMarketValueBreakdown && (
        <Box>
          <Label mb="4">Total Current Market Value</Label>

          <Text fontWeight={500} variant="tabular">
            {formatCurrency(
              parsePositionValue(specificPositionsValue) +
                parsePositionValue(nonSpecificPositionsValue),
            )}
          </Text>
        </Box>
      )}
    </>
  );
};
